<template>
  <PublicLayout :page-title="service?.name || 'Detalle del Servicio'">
    <div class="service-detail">
      <!-- Hero Section V3 Storytelling -->
      <section v-if="service" class="relative py-24 overflow-hidden">
        <!-- Background blobs -->
        <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-accent-50/30" />
        <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-teal-200/20 rounded-full blur-3xl" />

        <div class="relative max-w-6xl mx-auto px-6 text-center z-10">
          <!-- Badge -->
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-accent-50
                      border border-accent-200 rounded-full text-accent-700
                      text-sm font-medium mb-6 animate-fade-up">
            <component :is="service.icon" class="w-4 h-4" aria-hidden="true" />
            {{ getTierLabel(service.tier) }}
          </div>

          <!-- Title -->
          <h1 class="text-5xl md:text-6xl font-bold text-text-primary
                     tracking-tight mb-6 animate-fade-up delay-100">
            {{ service.name }}
          </h1>

          <!-- Subtitle -->
          <p class="text-xl md:text-2xl text-text-secondary max-w-2xl mx-auto
                    mb-8 animate-fade-up delay-200">
            {{ service.shortDescription }}
          </p>

          <!-- Trust indicators -->
          <div class="flex flex-wrap items-center justify-center gap-6
                      text-sm text-text-secondary animate-fade-up delay-300">
            <div class="flex items-center gap-2">
              <ShieldCheckIcon class="w-5 h-5 text-accent-500" aria-hidden="true" />
              <span>GDPR Compliant</span>
            </div>
            <div class="flex items-center gap-2">
              <LockClosedIcon class="w-5 h-5 text-accent-500" aria-hidden="true" />
              <span>Datos Cifrados</span>
            </div>
            <div class="flex items-center gap-2">
              <ClockIcon class="w-5 h-5 text-accent-500" aria-hidden="true" />
              <span>Respuesta rápida</span>
            </div>
          </div>

          <!-- Price Card -->
          <div class="mt-12 animate-fade-up delay-400">
            <div class="inline-block bg-white rounded-2xl
                        px-8 py-6 border border-slate-200 shadow-lg
                        hover:shadow-xl transition-shadow duration-300">
              <div class="text-sm text-text-secondary mb-1">Desde</div>
              <div class="text-4xl font-bold text-text-primary">
                {{ service.price }}€
              </div>
              <div class="text-sm text-accent-600 font-medium">Pago único</div>
            </div>
          </div>

          <!-- CTA Buttons -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center mt-10 animate-fade-up delay-500">
            <button
              @click="requestQuote"
              class="inline-flex items-center justify-center px-8 py-4
                     bg-gradient-to-r from-accent-600 to-teal-500
                     text-white font-semibold rounded-full
                     shadow-lg shadow-accent-500/25
                     hover:shadow-xl hover:shadow-accent-500/30 hover:scale-[1.02]
                     transition-all duration-300"
            >
              <SparklesIcon class="w-5 h-5 mr-2" aria-hidden="true" />
              Comenzar Ahora
            </button>
            <button
              @click="scrollToFeatures"
              class="inline-flex items-center justify-center px-8 py-4
                     border-2 border-accent-500 text-accent-600
                     font-semibold rounded-2xl bg-white
                     hover:bg-accent-50 hover:scale-[1.02]
                     transition-all duration-300"
            >
              Ver características
            </button>
          </div>
        </div>
      </section>

      <!-- Description Section -->
      <section v-if="service" class="py-16 bg-white">
        <div class="max-w-4xl mx-auto px-6">
          <div class="bg-white rounded-2xl p-8
                      border border-slate-200 shadow-lg animate-fade-up">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Sobre este servicio
            </h2>
            <p class="text-lg text-text-secondary leading-relaxed">
              {{ service.description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section ref="featuresSection" class="py-16 bg-slate-50">
        <div class="max-w-6xl mx-auto px-6">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4 animate-fade-up">
              ¿Qué incluye el servicio?
            </h2>
            <p class="text-xl text-text-secondary animate-fade-up delay-100">
              Todo lo que necesitas para {{ service?.name?.toLowerCase() }}
            </p>
          </div>

          <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="(feature, index) in service?.features"
              :key="feature"
              class="bg-white rounded-2xl p-6
                     border border-slate-200 shadow-lg
                     hover:shadow-xl hover:border-accent-300
                     transition-all duration-300 group animate-fade-up"
              :style="{ animationDelay: `${(index % 6) * 100}ms` }"
            >
              <div class="flex items-start gap-4">
                <div class="w-10 h-10 bg-accent-50 rounded-xl
                            flex items-center justify-center flex-shrink-0
                            group-hover:scale-110 transition-transform duration-300">
                  <CheckCircleIcon class="w-5 h-5 text-accent-600" aria-hidden="true" />
                </div>
                <p class="text-text-primary font-medium">{{ feature }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Benefits Section -->
      <section v-if="service?.benefits" class="py-16 bg-white">
        <div class="max-w-6xl mx-auto px-6">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4 animate-fade-up">
              Beneficios Clave
            </h2>
            <p class="text-xl text-text-secondary animate-fade-up delay-100">
              Por qué elegir este servicio
            </p>
          </div>

          <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="(benefit, index) in service.benefits"
              :key="benefit"
              class="bg-gradient-to-br from-accent-50 to-teal-50 rounded-2xl p-6
                     border border-accent-100
                     hover:shadow-lg transition-all duration-300 animate-fade-up"
              :style="{ animationDelay: `${(index % 6) * 100}ms` }"
            >
              <div class="flex items-start gap-4">
                <div class="w-10 h-10 bg-white rounded-xl
                            flex items-center justify-center flex-shrink-0 shadow-sm">
                  <StarIcon class="w-5 h-5 text-accent-600" aria-hidden="true" />
                </div>
                <p class="text-text-primary font-medium">{{ benefit }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Service-Specific FAQ -->
      <section class="py-16 bg-white">
        <div class="max-w-4xl mx-auto px-6">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4 animate-fade-up">
              Preguntas Frecuentes
            </h2>
            <p class="text-xl text-text-secondary animate-fade-up delay-100">
              Resolvemos tus dudas sobre {{ service?.name }}
            </p>
          </div>

          <ServiceFAQ @request-quote="requestQuote" />
        </div>
      </section>

      <!-- CTA Section -->
      <section class="relative py-24 overflow-hidden">
        <!-- Gradient background -->
        <div class="absolute inset-0 bg-gradient-to-br from-accent-600 to-teal-600" />
        <!-- Decorative blobs -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div class="absolute bottom-0 right-1/4 w-80 h-80 bg-white rounded-full blur-3xl" />
        </div>

        <div class="relative max-w-4xl mx-auto px-6 text-center z-10">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fade-up">
            ¿Listo para comenzar?
          </h2>
          <p class="text-xl text-white/80 mb-8 animate-fade-up delay-100">
            Solicita tu presupuesto personalizado sin compromiso
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up delay-200">
            <button
              @click="requestQuote"
              class="inline-flex items-center justify-center px-8 py-4
                     bg-white text-accent-600 font-bold rounded-2xl
                     hover:bg-white/90 hover:scale-[1.05]
                     transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <SparklesIcon class="w-5 h-5 mr-2" aria-hidden="true" />
              Solicitar presupuesto
            </button>
            <button
              @click="goToServices"
              class="inline-flex items-center justify-center px-8 py-4
                     border-2 border-white text-white font-semibold rounded-2xl
                     hover:bg-white hover:text-accent-600
                     transition-all duration-300"
            >
              Ver otros servicios
            </button>
          </div>
        </div>
      </section>

      <!-- Success Alert -->
      <Alert
        v-if="showSuccessAlert"
        variant="success"
        title="¡Solicitud Enviada!"
        :message="successMessage"
        :dismissible="true"
        :auto-close="true"
        :auto-close-delay="5000"
        @dismiss="showSuccessAlert = false"
      />
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import { useSchemaOrg, createLegalServiceSchema } from '@/composables/useSchemaOrg'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import Alert from '@/components/ui/Alert.vue'
import ServiceFAQ from '@/components/services/ServiceFAQ.vue'
import {
  CheckCircleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  ClockIcon,
  SparklesIcon,
  StarIcon
} from '@heroicons/vue/24/outline'
import { getServiceById } from '@/utils/constants/services'

// Props
const props = defineProps({
  serviceId: String
})

// Router
const route = useRoute()
const router = useRouter()

// Reactive state
const showSuccessAlert = ref(false)
const successMessage = ref('')
const featuresSection = ref(null)

// Computed - Get service from centralized constants
const service = computed(() => {
  const serviceId = props.serviceId || route.params.serviceId
  return getServiceById(serviceId)
})

// SEO Meta Tags - Dynamic per service
const seoConfig = {
  'derecho-olvido': {
    title: 'Derecho al Olvido - Elimina tus Datos',
    description: 'Servicio profesional de eliminación de datos según RGPD Art. 17. Gestión integral con abogados. Desde 499€.'
  },
  'derecho-olvido-postmortem': {
    title: 'Derecho al Olvido Post-Mortem',
    description: 'Gestión de datos de personas fallecidas. Eliminación de cuentas y perfiles con validez legal. Desde 699€.'
  },
  'testamento-digital': {
    title: 'Testamento Digital - Legado Online',
    description: 'Crea tu testamento digital. Designa herederos, albaceas y gestiona tu patrimonio digital. Desde 599€.'
  }
}

const currentSeo = computed(() => {
  const serviceId = props.serviceId || route.params.serviceId
  return seoConfig[serviceId] || seoConfig['derecho-olvido']
})

useSeo({
  title: computed(() => currentSeo.value.title),
  description: computed(() => currentSeo.value.description),
  canonical: computed(() => `/services/${props.serviceId || route.params.serviceId}`)
})

// Schema.org LegalService (SEO Phase 4)
const serviceSchemaData = {
  'derecho-olvido': {
    name: 'Derecho al Olvido',
    description: 'Servicio profesional de eliminación de datos personales de internet bajo GDPR Artículo 17. Gestionamos solicitudes legales para eliminar información de buscadores y plataformas.',
    url: '/services/derecho-olvido',
    serviceType: 'Right to be Forgotten (GDPR Article 17)',
    price: '499',
    areaOfLaw: ['GDPR', 'Data Protection Law', 'Digital Rights']
  },
  'derecho-olvido-postmortem': {
    name: 'Derecho al Olvido Post-Mortem',
    description: 'Gestión del legado digital de personas fallecidas. Ayudamos a familiares a eliminar, memorializar o recuperar contenidos digitales respetando derechos forales.',
    url: '/services/derecho-olvido-postmortem',
    serviceType: 'Digital Legacy Management (Deceased Persons)',
    price: '699',
    areaOfLaw: ['GDPR', 'Digital Estate Law', 'Foral Rights']
  },
  'testamento-digital': {
    name: 'Testamento Digital',
    description: 'Planificación integral de legado digital. Crea instrucciones testamentarias, designa herederos digitales y establece cláusulas para gestión de patrimonio digital.',
    url: '/services/testamento-digital',
    serviceType: 'Digital Will and Testament',
    price: '599',
    areaOfLaw: ['Digital Estate', 'Succession Law']
  }
}

const currentSchemaData = computed(() => {
  const serviceId = props.serviceId || route.params.serviceId
  return serviceSchemaData[serviceId]
})

useSchemaOrg(computed(() =>
  currentSchemaData.value ? createLegalServiceSchema(currentSchemaData.value) : null
))

// Helper functions
const getTierLabel = (tier) => {
  const labels = {
    olvido: 'Derecho al Olvido',
    olvido_postmortem: 'Post-Mortem',
    testamento: 'Testamento Digital'
  }
  return labels[tier] || 'Servicio Legal'
}

// Methods - Direct registration flow (ADR-106)
const requestQuote = (tier = null) => {
  const query = tier ? { plan: tier } : { plan: service.value?.tier }
  router.push({
    path: '/auth/register',
    query
  })
}

const scrollToFeatures = () => {
  if (featuresSection.value) {
    featuresSection.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
}

const goToServices = () => {
  router.push('/pricing')
}

// Handle service not found
onMounted(() => {
  if (!service.value) {
    router.replace('/pricing')
  }
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.service-detail {
  @apply min-h-screen bg-white;
}

/* Animation utilities */
.animate-fade-up {
  animation: fadeUp 0.6s ease-out forwards;
  opacity: 0;
}

.delay-100 {
  animation-delay: 100ms;
}

.delay-200 {
  animation-delay: 200ms;
}

.delay-300 {
  animation-delay: 300ms;
}

.delay-400 {
  animation-delay: 400ms;
}

.delay-500 {
  animation-delay: 500ms;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  .animate-fade-up {
    animation: none;
    opacity: 1;
  }

  .service-detail * {
    transition-duration: 0.01ms !important;
  }
}

/* High Contrast Support */
@media (prefers-contrast: high) {
  .border-slate-200\/50 {
    border-color: theme('colors.slate.400');
  }
}
</style>
